// Canonical regulated enum values — the single source of truth that MUST match
// the live Postgres CHECK allow-lists. Forms, read-side badge/filter maps, and
// canonicalisers should reference these instead of hardcoding string arrays.
//
// This file is the structural gate against the "vocabulary-mismatch" defect
// class (2026-05-29 sweep): a caller/reader vocabulary diverging from what the
// DB CHECK enforces, which manifests as hard 23514 form rejects, silent
// mis-stores, no-op gates, or dead read-side tokens.
//
// Two regression nets consume this:
//   • apps/web/src/__tests__/regulated-vocabulary-contract.test.ts — asserts
//     every listed form's option array is a subset of the canonical values
//     (static, runs in CI without a DB).
//   • scripts/check-vocabulary-contracts.mjs — verifies each canonical set
//     equals the LIVE DB CHECK allow-list (on-demand; needs DB creds).
//
// When the DB CHECK changes, update the canonical const here AND run the script
// to confirm parity; the contract test then keeps every form in line.

export const WHS_INCIDENT_TYPES = [
  'INJURY',
  'NEAR_MISS',
  'HAZARD',
  'EXPOSURE',
  'MANUAL_HANDLING',
  'SLIP_TRIP_FALL',
  'AGGRESSION',
  'NEEDLE_STICK',
  'OTHER',
  'VEHICLE',
] as const;

export const WHS_INCIDENT_SEVERITIES = ['MINOR', 'MODERATE', 'SERIOUS', 'CRITICAL'] as const;

export const WHS_HAZARD_TYPES = [
  'PHYSICAL',
  'CHEMICAL',
  'BIOLOGICAL',
  'ERGONOMIC',
  'MANUAL_HANDLING',
  'PSYCHOSOCIAL',
  'ELECTRICAL',
  'ENVIRONMENTAL',
  'OTHER',
] as const;

export const WHS_EQUIPMENT_TYPES = [
  'LIFTING',
  'MOBILITY',
  'FIRE_SAFETY',
  'PPE',
  'MEDICAL',
  'KITCHEN',
  'CLEANING',
  'VEHICLE',
  'OTHER',
] as const;

export const RISK_REGISTER_STATUSES = [
  'OPEN',
  'MITIGATED',
  'ACCEPTED',
  'CLOSED',
  'ESCALATED',
] as const;

export const RISK_REGISTER_RATINGS = ['LOW', 'MEDIUM', 'HIGH', 'EXTREME'] as const;

export const TRAINING_RECORD_CATEGORIES = [
  'MANUAL_HANDLING',
  'INFECTION_CONTROL',
  'FIRE_SAFETY',
  'MEDICATION',
  'DEMENTIA',
  'PALLIATIVE',
  'ELDER_ABUSE',
  'CPR_FIRST_AID',
  'WHS',
  'FOOD_SAFETY',
  'RESTRICTIVE_PRACTICES',
  'CULTURAL_SAFETY',
  'OTHER',
] as const;

export const TRAINING_RECORD_TYPES = [
  'MANDATORY',
  'ELECTIVE',
  'PROFESSIONAL_DEVELOPMENT',
  'INDUCTION',
  'REFRESHER',
] as const;

// whs_hazards.status carries TWO CHECKs (chk_whs_hazards_status allows
// MONITORING; whs_hazards_status_check does not) — a row must satisfy both, so
// the effective allow-list is the INTERSECTION (no MONITORING).
export const WHS_HAZARD_STATUSES = ['OPEN', 'ASSESSED', 'CONTROLLED', 'CLOSED'] as const;

export const WHS_HAZARD_RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'EXTREME'] as const;

export const RISK_REGISTER_CATEGORIES = [
  'CLINICAL',
  'OPERATIONAL',
  'FINANCIAL',
  'REGULATORY',
  'REPUTATIONAL',
  'ENVIRONMENTAL',
  'WORKFORCE',
  'TECHNOLOGY',
] as const;

export const RISK_REGISTER_LIKELIHOODS = [
  'RARE',
  'UNLIKELY',
  'POSSIBLE',
  'LIKELY',
  'ALMOST_CERTAIN',
] as const;

export const RISK_REGISTER_CONSEQUENCES = [
  'INSIGNIFICANT',
  'MINOR',
  'MODERATE',
  'MAJOR',
  'CATASTROPHIC',
] as const;

export type WhsIncidentType = (typeof WHS_INCIDENT_TYPES)[number];
export type WhsIncidentSeverity = (typeof WHS_INCIDENT_SEVERITIES)[number];
export type WhsHazardType = (typeof WHS_HAZARD_TYPES)[number];
export type WhsEquipmentType = (typeof WHS_EQUIPMENT_TYPES)[number];
export type RiskRegisterStatus = (typeof RISK_REGISTER_STATUSES)[number];
export type RiskRegisterRating = (typeof RISK_REGISTER_RATINGS)[number];
export type TrainingRecordCategory = (typeof TRAINING_RECORD_CATEGORIES)[number];
export type TrainingRecordType = (typeof TRAINING_RECORD_TYPES)[number];
export type WhsHazardStatus = (typeof WHS_HAZARD_STATUSES)[number];
export type WhsHazardRiskLevel = (typeof WHS_HAZARD_RISK_LEVELS)[number];
export type RiskRegisterCategory = (typeof RISK_REGISTER_CATEGORIES)[number];
export type RiskRegisterLikelihood = (typeof RISK_REGISTER_LIKELIHOODS)[number];
export type RiskRegisterConsequence = (typeof RISK_REGISTER_CONSEQUENCES)[number];

/**
 * Contract manifest: maps each canonical regulated enum to (a) the live DB
 * table+column whose CHECK it must equal, and (b) the form option arrays that
 * must stay a subset of it. Consumed by the contract test + the live-check
 * script. Add a row whenever a new regulated CHECK-constrained dropdown ships.
 */
export interface RegulatedEnumContract {
  readonly key: string;
  readonly table: string;
  readonly column: string;
  readonly values: readonly string[];
  /**
   * Form option arrays that must be a subset of `values` (checked statically by
   * the contract test). `field` selects the object key to read for object-style
   * option arrays (e.g. `{ value: 'OPEN', label: 'Open' }` → field 'value';
   * `{ value: 1, enumValue: 'RARE' }` → field 'enumValue'); omit it for a plain
   * string array. May be empty for columns whose surface isn't statically
   * parseable (read-side badge maps) — those stay guarded against DB drift by
   * the live-check script, which validates every contract's `values` vs CHECK.
   */
  readonly forms: ReadonlyArray<{
    readonly file: string;
    readonly arrayName: string;
    readonly field?: string;
  }>;
}

export const REGULATED_ENUM_CONTRACTS: readonly RegulatedEnumContract[] = [
  {
    key: 'whs_incident_type',
    table: 'whs_incidents',
    column: 'incident_type',
    values: WHS_INCIDENT_TYPES,
    forms: [
      {
        file: 'apps/web/src/lib/compliance/whs-record.ts',
        arrayName: 'WHS_INCIDENT_TYPES',
      },
    ],
  },
  {
    key: 'whs_incident_severity',
    table: 'whs_incidents',
    column: 'severity',
    values: WHS_INCIDENT_SEVERITIES,
    forms: [
      {
        file: 'apps/web/src/lib/compliance/whs-record.ts',
        arrayName: 'WHS_SEVERITIES',
      },
    ],
  },
  {
    key: 'whs_hazard_type',
    table: 'whs_hazards',
    column: 'hazard_type',
    values: WHS_HAZARD_TYPES,
    forms: [
      {
        file: 'apps/web/src/app/(dashboard)/compliance/whs/hazards/_components/hazard-fields.tsx',
        arrayName: 'hazardTypes',
      },
    ],
  },
  {
    key: 'training_record_category',
    table: 'training_records',
    column: 'category',
    values: TRAINING_RECORD_CATEGORIES,
    // The legacy free-form writer is retired. Keep the live CHECK vocabulary
    // registered, but do not name a deleted compatibility route as a producer.
    forms: [],
  },
  {
    key: 'training_record_type',
    table: 'training_records',
    column: 'training_type',
    values: TRAINING_RECORD_TYPES,
    // External evidence now enters through the governed Phase 3 contract rather
    // than writing these legacy tokens directly.
    forms: [],
  },
  // These columns are enforced live but their app surfaces aren't simple option
  // arrays (whs_equipment uses an inline <select>; risk status/rating are
  // read-side badge maps), so they carry no static form binding yet. They are
  // still guarded against DB drift by `pnpm check:vocabulary-contracts`.
  {
    key: 'whs_equipment_type',
    table: 'whs_equipment',
    column: 'equipment_type',
    values: WHS_EQUIPMENT_TYPES,
    forms: [],
  },
  {
    key: 'risk_register_status',
    table: 'risk_register',
    column: 'status',
    values: RISK_REGISTER_STATUSES,
    forms: [
      {
        file: 'apps/web/src/app/(dashboard)/compliance/governance/risk-register/page.tsx',
        arrayName: 'RISK_STATUS_OPTIONS',
        field: 'value',
      },
    ],
  },
  {
    key: 'risk_register_rating',
    table: 'risk_register',
    column: 'risk_rating',
    values: RISK_REGISTER_RATINGS,
    forms: [
      {
        file: 'apps/web/src/app/(dashboard)/compliance/governance/risk-register/page.tsx',
        arrayName: 'RISK_RATING_OPTIONS',
        field: 'value',
      },
    ],
  },
  {
    key: 'whs_hazard_status',
    table: 'whs_hazards',
    column: 'status',
    values: WHS_HAZARD_STATUSES,
    forms: [
      {
        file: 'apps/web/src/app/(dashboard)/compliance/whs/hazards/page.tsx',
        arrayName: 'HAZARD_STATUS_OPTIONS',
        field: 'value',
      },
    ],
  },
  {
    key: 'whs_hazard_risk_level',
    table: 'whs_hazards',
    column: 'risk_level',
    values: WHS_HAZARD_RISK_LEVELS,
    forms: [
      {
        file: 'apps/web/src/app/(dashboard)/compliance/whs/hazards/_components/hazard-fields.tsx',
        arrayName: 'riskLevels',
      },
      {
        file: 'apps/web/src/app/(dashboard)/compliance/whs/hazards/page.tsx',
        arrayName: 'HAZARD_RISK_OPTIONS',
        field: 'value',
      },
    ],
  },
  {
    key: 'risk_register_category',
    table: 'risk_register',
    column: 'risk_category',
    values: RISK_REGISTER_CATEGORIES,
    forms: [
      {
        file: 'apps/web/src/lib/governance/risk-register-enums.ts',
        arrayName: 'RISK_CATEGORIES',
      },
    ],
  },
  {
    key: 'risk_register_likelihood',
    table: 'risk_register',
    column: 'likelihood',
    values: RISK_REGISTER_LIKELIHOODS,
    forms: [
      {
        file: 'apps/web/src/lib/governance/risk-register-enums.ts',
        arrayName: 'RISK_LIKELIHOOD_ENUMS',
      },
    ],
  },
  {
    key: 'risk_register_consequence',
    table: 'risk_register',
    column: 'consequence',
    values: RISK_REGISTER_CONSEQUENCES,
    forms: [
      {
        file: 'apps/web/src/lib/governance/risk-register-enums.ts',
        arrayName: 'RISK_CONSEQUENCE_ENUMS',
      },
    ],
  },
];
